/**
 * Screen: System health.
 *
 * One question, answered above the fold: **is the data on every other screen current?**
 *
 * It exists because on 2026-08-24 the answer was "no" for three and a half days and nothing said
 * so. LSK's SQL Accounting bridge exhausted its memory, every sync call returned 500, and the
 * dashboard carried on showing frozen watermarks in the same grey as fresh ones next to a job that
 * reported "ok" 168 times running. Every fact was on screen; no judgement was.
 *
 * So this screen states a verdict in words, counts the outage in days and hours, and shows the
 * error the remote system actually returned — the string that turns "the sync is broken" into
 * "their server needs restarting".
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/api';
import { Card, Table, Loading, cell } from '../components/ui';
import { dateTime, duration, MYT, preciseTitle } from '../lib/time';

type SyncStatus = 'ok' | 'stale' | 'down' | 'unknown';

type JobHealth = {
  tenantId: string;
  sync: {
    overall: SyncStatus;
    systems: Array<{
      system: string;
      status: SyncStatus;
      worstResource: string | null;
      staleForMs: number | null;
    }>;
  };
  alerts: Array<{
    id: number;
    severity: string;
    code: string;
    message: string;
    createdAt: string | null;
  }>;
  scheduled: Array<{
    name: string;
    cron: string;
    description: string;
    lastRun: {
      status: string;
      startedAt: string | null;
      durationSeconds: number;
      error: string | null;
    } | null;
  }>;
  activeLocks: Array<{ jobName: string; lockedBy: string; lockedAt: string }>;
  stuckRuns: Array<{ jobName: string; runningForSeconds: number }>;
  cursors: Array<{
    resource: string;
    company: string;
    system: string;
    status: SyncStatus;
    staleForMs: number | null;
    watermarkAt: string | null;
    lastRunAt: string | null;
    lastOkAt: string | null;
    lastError: string | null;
    docsSeen: number;
  }>;
  recentRuns: Array<{
    id: number;
    jobName: string;
    status: string;
    startedAt: string | null;
    durationSeconds: number;
    error: string | null;
  }>;
};

/** Plain words, because colour alone fails a colour-blind reader and a pasted screenshot. */
const LABEL: Record<SyncStatus, string> = {
  ok: 'Current',
  stale: 'Falling behind',
  down: 'Down',
  unknown: 'Never run',
};

const TONE: Record<SyncStatus, string> = {
  ok: 'text-emerald-700',
  stale: 'text-amber-700',
  down: 'text-red-700',
  unknown: 'text-slate-500',
};

const BANNER: Record<SyncStatus, string> = {
  ok: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  stale: 'border-amber-300 bg-amber-50 text-amber-900',
  down: 'border-red-300 bg-red-50 text-red-900',
  unknown: 'border-slate-300 bg-slate-50 text-slate-700',
};

const SYSTEM_LABEL: Record<string, string> = {
  SQLACC: 'SQL Accounting',
  SMARTLINK: 'Smartlink',
};

/** What an operator should do about it — the gap between "it is broken" and "I can act". */
function advice(system: string, status: SyncStatus, lastError: string | null): string | null {
  if (status === 'ok' || status === 'unknown') return null;
  if (system === 'SQLACC' && lastError && /500/.test(lastError)) {
    // The IntraWeb bridge answers 500 when it runs out of memory, and nothing on our side fixes
    // it — the application has to be restarted on LSK's server. Saying so here saves the hour it
    // took to work out the first time.
    return 'LSK’s SQL Accounting bridge is returning errors. If these are HTTP 500, the IntraWeb application on their server has most likely run out of memory and needs restarting — nothing on our side will clear it.';
  }
  if (system === 'SMARTLINK') {
    return 'Scan collection is behind. Check that the scheduler is running and that the Smartlink database is reachable on port 11314.';
  }
  return 'The sync has stopped advancing. Check the most recent job run below for the error it returned.';
}

export default function SystemHealth() {
  const health = useQuery({
    queryKey: ['system-health'],
    queryFn: () => api.get<JobHealth>('/api/v1/health/jobs'),
    refetchInterval: 30_000,
  });

  const overall = health.data?.sync.overall ?? 'unknown';

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold text-slate-900">System health</h1>
        <p className="text-sm text-slate-500">
          Whether the data on every other screen is current. Times in {MYT}; refreshes every 30s.
        </p>
      </header>

      {/* An unreachable API must never render as a blank — and a blank health screen reads
          exactly like a healthy one, which is the failure this page exists to end. */}
      {health.error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-900">Health API unreachable</p>
          <p className="mt-1 text-sm text-red-800">
            {(health.error as Error).message}. The middleware itself may be down — this screen
            cannot tell you about a sync it cannot reach.
          </p>
        </div>
      )}

      {health.isLoading && <Loading />}

      {health.data && (
        <>
          <div
            className={`rounded-lg border p-4 ${BANNER[overall]}`}
            {...(overall === 'down' ? { 'data-testid': 'sync-banner-down' } : {})}
          >
            <p className="text-sm font-semibold uppercase tracking-wide">
              {overall === 'ok' ? 'All syncs current' : `Sync status: ${LABEL[overall]}`}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {health.data.sync.systems.map((s) => (
                <div
                  key={s.system}
                  data-testid={`system-${s.system}`}
                  className="rounded border border-white/60 bg-white/70 p-3"
                >
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {SYSTEM_LABEL[s.system] ?? s.system}
                  </p>
                  <p className={`text-sm font-semibold ${TONE[s.status]}`}>
                    {LABEL[s.status]}
                    {s.status !== 'ok' && s.staleForMs !== null && (
                      <span className="font-normal"> · stalled {duration(s.staleForMs)}</span>
                    )}
                    {s.status === 'ok' && s.staleForMs !== null && (
                      <span className="font-normal text-slate-500">
                        {' '}
                        · last synced {duration(s.staleForMs)} ago
                      </span>
                    )}
                  </p>
                  {s.worstResource && s.status !== 'ok' && (
                    <p className="mt-1 text-xs text-slate-600">
                      Worst: <span className="font-mono">{s.worstResource}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>

            {health.data.sync.systems
              .map((s) => {
                const cursorErr =
                  health.data!.cursors.find((c) => c.resource === s.worstResource)?.lastError ??
                  null;
                return { system: s.system, text: advice(s.system, s.status, cursorErr) };
              })
              .filter((a) => a.text !== null)
              .map((a) => (
                <p key={a.system} className="mt-3 text-sm">
                  {a.text}
                </p>
              ))}
          </div>

          {health.data.stuckRuns.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
              {health.data.stuckRuns.length} job(s) running for over 12 hours — likely stuck:{' '}
              {health.data.stuckRuns.map((s) => s.jobName).join(', ')}
            </div>
          )}

          {health.data.alerts.length > 0 && (
            <Card title="Open alerts" subtitle="Cleared automatically once the condition passes">
              <Table
                head={['Raised', 'Severity', 'Code', 'Message']}
                rows={health.data.alerts.map((a) => [
                  cell(
                    a.createdAt ?? '',
                    <span title={preciseTitle(a.createdAt)}>{dateTime(a.createdAt)}</span>,
                  ),
                  a.severity,
                  <span className="font-mono text-xs">{a.code}</span>,
                  a.message,
                ])}
              />
            </Card>
          )}

          <Card
            title="Sync cursors"
            subtitle="One row per resource we pull. “Last success” is the number that matters — a watermark can look recent while every run since has failed."
          >
            <Table
              head={['Resource', 'System', 'Status', 'Last success', 'Watermark', 'Docs', 'Error']}
              rows={health.data.cursors.map((c) => [
                <span className="font-mono text-xs">{c.resource}</span>,
                SYSTEM_LABEL[c.system] ?? c.system,
                cell(
                  c.status,
                  <span className={`font-medium ${TONE[c.status]}`}>{LABEL[c.status]}</span>,
                ),
                cell(
                  c.staleForMs ?? Number.MAX_SAFE_INTEGER,
                  <span title={preciseTitle(c.lastOkAt)}>
                    {c.lastOkAt === null ? 'never' : `${duration(c.staleForMs)} ago`}
                  </span>,
                ),
                cell(
                  c.watermarkAt ?? '',
                  <span title={preciseTitle(c.watermarkAt)}>{dateTime(c.watermarkAt)}</span>,
                ),
                c.docsSeen.toLocaleString(),
                c.lastError ? (
                  <span className="font-mono text-xs text-red-700">{c.lastError}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                ),
              ])}
            />
          </Card>

          <Card title="Scheduled jobs" subtitle={`Last run of each, in ${MYT}`}>
            <Table
              head={['Job', 'Schedule', 'Last run', 'Status', 'Error']}
              rows={health.data.scheduled.map((j) => [
                <span className="font-mono text-xs">{j.name}</span>,
                <span className="font-mono text-xs text-slate-500">{j.cron}</span>,
                cell(
                  j.lastRun?.startedAt ?? '',
                  <span title={preciseTitle(j.lastRun?.startedAt)}>
                    {j.lastRun
                      ? `${dateTime(j.lastRun.startedAt)} · ${j.lastRun.durationSeconds}s`
                      : 'never run'}
                  </span>,
                ),
                j.lastRun ? (
                  <span className={j.lastRun.status === 'ok' ? 'text-emerald-700' : 'text-red-700'}>
                    {j.lastRun.status}
                  </span>
                ) : (
                  <span className="text-slate-400">—</span>
                ),
                j.lastRun?.error ? (
                  <span className="font-mono text-xs text-red-700">{j.lastRun.error}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                ),
              ])}
            />
            {health.data.activeLocks.length > 0 && (
              <p className="mt-3 text-xs text-slate-500">
                Holding a lock: {health.data.activeLocks.map((l) => l.jobName).join(', ')}
              </p>
            )}
          </Card>

          <Card title="Recent runs" subtitle="Newest first">
            <Table
              head={['Started', 'Job', 'Status', 'Duration', 'Error']}
              rows={health.data.recentRuns.map((r) => [
                cell(
                  r.startedAt ?? '',
                  <span title={preciseTitle(r.startedAt)}>{dateTime(r.startedAt)}</span>,
                ),
                <span className="font-mono text-xs">{r.jobName}</span>,
                <span className={r.status === 'ok' ? 'text-emerald-700' : 'text-red-700'}>
                  {r.status}
                </span>,
                `${r.durationSeconds}s`,
                r.error ? (
                  <span className="font-mono text-xs text-red-700">{r.error}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                ),
              ])}
            />
          </Card>
        </>
      )}
    </div>
  );
}
